<template>
    <v-form
        :ref="formRef"
        v-model="form.valid"
        lazy-validation
    >
        <v-dialog v-model="dialogState" fullscreen hide-overlay @keydown.esc="close()">
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    :loading="loading"
                    :disabled="loading"
                    :dark="!loading"
                    class="mr-1"
                    :color="buttonColor"
                    v-bind="attrs"
                    v-on="on"
                    :outlined="!isNew"
                >
                    <v-icon v-if="!isNew" class="mr-1">edit</v-icon>
                    {{ buttonLabel }}
                </v-btn>
            </template>

            <v-card min-height="100vh">
                <v-toolbar dark color="primary">
                    <v-btn icon dark @click.native="close()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>{{ form.title }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn
                            dark
                            text
                            :disabled="disabledSave()"
                            :loading="btnDisable"
                            @click.native="save()"
                        >{{ $t('common.save') }}</v-btn>
                    </v-toolbar-items>
                </v-toolbar>

                <v-container>
                    <v-card-title>
                        <v-subheader>{{ $t('corporate.captable.form.data') }}</v-subheader>
                    </v-card-title>

                    <v-divider></v-divider>

                    <v-progress-linear :indeterminate="true" v-show="form.loading"></v-progress-linear>

                    <div v-show="!form.loading">
                        <v-row>
                            <v-col cols="12" class="pt-1 pb-1">
                                <v-text-field
                                    v-model="captable.title"
                                    :label="form.labels.title"
                                    :rules="form.rules.title"
                                    required
                                    filled
                                    dense
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row no-gutters>
                            <v-col cols="12" class="pb-1">
                                <v-divider class="mb-5"></v-divider>
                                <div class="d-flex justify-space-between align-center">
                                    <span class="title">
                                        <v-icon>group</v-icon>
                                        {{ form.memberType }}
                                    </span>
                                    <v-spacer/>
                                    <v-switch
                                        v-if="showGroupEntries"
                                        v-model="groupEntries"
                                        :label="$t('corporate.captable.form.groupEntry')"
                                        class="mr-1"
                                    ></v-switch>
                                    <v-btn
                                        class="ml-1"
                                        color="primary"
                                        @click.native="addPartner()"
                                        :disabled="!addPartnerAvaible"
                                    >
                                        {{ $t('common.add') }}
                                    </v-btn>
                                </div>
                                <v-divider class="mt-4"></v-divider>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6" class="pt-1 pb-1">
                                {{ $t('corporate.captable.form.columns.name') }}
                            </v-col>
                            <v-col cols="2" class="pt-1 pb-1 text-center">
                                {{ form.shareType }}
                            </v-col>
                            <v-col cols="2" class="pt-1 pb-1 text-center">
                                {{ $t('corporate.captable.form.columns.percentual') }}
                            </v-col>
                            <v-spacer></v-spacer>
                        </v-row>
                        <v-divider class="mb-4"></v-divider>
                        <div class="member-container">
                            <template v-if="captable.partners.length <= 0">
                                <div class="text-center"><span style="color: red">{{ $t('corporate.captable.form.empty') }}</span></div>
                            </template>
                            <template v-else v-for="(item, index) in captable.partners">
                                <v-row :key="index" align="center">
                                    <v-col cols="6" class="pt-1 pb-1">
                                        <v-text-field
                                            v-model="item.name"
                                            :label="form.labels.required"
                                            :rules="form.rules.member"
                                            required
                                            filled
                                            dense
                                            hide-details
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="2" class="pt-1 pb-1">
                                        <v-text-field
                                            v-model="item.shares"
                                            :label="form.labels.required"
                                            :rules="form.rules.shares"
                                            @change="(!groupEntries) ? calcByRow(index, true, true) : undefined"
                                            class="text-right"
                                            required
                                            filled
                                            dense
                                            hide-details
                                            reverse
                                            type="number"
                                            min="0"
                                            :disabled="item.dirty || groupEntries"
                                            :readonly="item.dirty || groupEntries"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="2" class="pt-1 pb-1">
                                        <v-text-field
                                            v-if="!item.raising"
                                            v-model="item.percentual"
                                            :label="form.labels.required"
                                            :rules="form.rules.percentual"
                                            @change="(!groupEntries) ? calcByRow(index, false, true) : undefined"
                                            class="text-right"
                                            required
                                            filled
                                            dense
                                            hide-details
                                            reverse
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            max="100"
                                            :disabled="item.dirty || initialShares === 0 || (groupEntries && !item.new)"
                                            :readonly="item.dirty || initialShares === 0 || (groupEntries && !item.new)"
                                        ></v-text-field>
                                        <v-text-field
                                            v-else
                                            v-model="item.raisingPercentual"
                                            :label="form.labels.required"
                                            :rules="form.rules.percentual"
                                            @change="(!groupEntries) ? calcByRow(index, false, true) : undefined"
                                            class="text-right"
                                            required
                                            filled
                                            dense
                                            hide-details
                                            reverse
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            max="100"
                                            :disabled="item.dirty || initialShares === 0"
                                            :readonly="item.dirty || initialShares === 0"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="2" class="pt-1 pb-1 text-right">
                                        <v-btn
                                            v-if="groupEntries && !item.dirty"
                                            icon
                                            color="secondary"
                                            @click.prevent="toggleRaising(index)"
                                        >
                                            <v-icon v-if="item.raising">mdi-account</v-icon>
                                            <v-icon v-else>mdi-account-multiple</v-icon>
                                        </v-btn>
                                        <v-btn
                                            v-if="showToggleDirty(index)"
                                            icon
                                            color="secondary"
                                            @click.prevent="toggleDirty(index)"
                                        >
                                            <v-icon v-if="item.dirty">mdi-lock</v-icon>
                                            <v-icon v-else>mdi-lock-open</v-icon>
                                        </v-btn>
                                        <v-btn
                                            icon
                                            color="error"
                                            @click.prevent="deletePartner(index, item)"
                                        >
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </template>
                        </div>
                        <div class="captable-total-container">
                            <v-container>
                                <v-row align="center">
                                    <v-col cols="12" class="pt-0 pb-2 text-color-red">
                                        {{ $t('corporate.captable.form.shareNumberRounded').replace('%form.shareType%', form.shareType) }}
                                    </v-col>
                                    <v-col cols="2" class="pt-1 pb-1">
                                        <v-text-field
                                            v-model="initialShares"
                                            :label="form.labels.shareCapitalInitial"
                                            required
                                            hide-details
                                            dense
                                            readonly
                                            disabled
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="2" class="pt-1 pb-1">
                                        <v-text-field
                                            v-model="baseShares"
                                            :label="form.labels.shareCapitalBase"
                                            :rules="form.rules.shareCapitalBase"
                                            @change="recalcRows()"
                                            required
                                            hide-details
                                            dense
                                            readonly
                                            disabled
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="2" class="pt-1 pb-1">
                                        <v-btn
                                            v-if="!groupEntries && initialShares !== 0"
                                            small
                                            text
                                            elevation="0"
                                            color="secondary"
                                            @click.prevent="toggleBlock()"
                                        >
                                            <template v-if="baseSharesBlock">
                                                <v-icon>mdi-lock</v-icon>
                                                {{ $t('corporate.captable.form.unlockChanges') }}
                                            </template>
                                            <template v-else>
                                                <v-icon>mdi-lock-open</v-icon>
                                                {{ $t('corporate.captable.form.lockChanges') }}
                                            </template>
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="2" class="pt-1 pb-1 text-right" :class="{ 'text-color-red': baseSharesBlock && sumShares > baseShares }">
                                        <!-- ficar vermelho se passar da baseShares -->
                                        {{ sumShares }}
                                    </v-col>
                                    <v-col cols="2" class="pt-1 pb-1 text-right" :class="{ 'text-color-red': baseSharesBlock && sumShares > baseShares }">
                                        <!-- ficar vermelho se passar de 100% -->
                                        {{ sumPercentual }}%
                                    </v-col>
                                    <v-col
                                        v-if="groupEntries"
                                        cols="2"
                                        class="pt-1 pb-1 text-right"
                                    >
                                        <v-btn
                                            small
                                            color="secondary"
                                            class="pl-1 pr-1 mr-1"
                                            @click.prevent="calcGroup()"
                                        >
                                            {{ $t('corporate.captable.form.calc') }}
                                        </v-btn>
                                        <v-btn
                                            small
                                            color="error"
                                            class="pl-1 pr-1 ml-1"
                                            @click.prevent="returnToInit()"
                                        >
                                            {{ $t('corporate.captable.form.reInit') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </div>
                    </div>
                </v-container>
            </v-card>
        </v-dialog>
    </v-form>
</template>

<script>
import API from '@/api'
export default {
    name: 'modal-captable',
    props: ['loading', 'client', 'shareCapital', 'companyInfo', 'currentCaptable', 'isNew'],
    components: {},
    directives: {},
    data: function () {
        return {
            dialogState: false,
            btnDisable: false,
            addPartnerAvaible: true,
            form: {
                ref: '',
                loading: true,
                valid: false,
                title: this.$t('corporate.captable.form.titleNew'),
                memberType: this.$t('corporate.captable.form.memberTypeShareholders'),
                shareType: this.$t('corporate.captable.form.shareTypeActions'),
                labels: {
                    title: this.$t('corporate.captable.form.title.label'),
                    shareCapitalInitial: this.$t('corporate.captable.form.shareCapitalInitial.label'),
                    shareCapitalBase: this.$t('corporate.captable.form.shareCapitalBase.label'),
                    required: this.$t('corporate.captable.form.required')
                },
                rules: {
                    title: [
                        v => !!v || this.$t('corporate.captable.form.title.required'),
                        v => (v && v.length >= 3) || this.$t('corporate.captable.form.length.length')
                    ],
                    shareCapitalInitial: [
                        v => !!v || this.$t('corporate.captable.form.shareCapitalInitial.required'),
                        v => !(v < 0) || this.$t('corporate.captable.form.shareCapitalInitial.length')
                    ],
                    shareCapitalBase: [
                        v => !!v || this.$t('corporate.captable.form.shareCapitalBase.required'),
                        v => !(v < 0) || this.$t('corporate.captable.form.shareCapitalBase.length')
                    ],
                    member: [
                        v => !!v || this.$t('corporate.captable.form.member.required'),
                        v => (v && v.length >= 3) || this.$t('corporate.captable.form.member.length')
                    ],
                    shares: [
                        v => !!v || this.$t('corporate.captable.form.shares.required'),
                        v => !(v < 0) || this.$t('corporate.captable.form.shares.length')
                    ],
                    percentual: [
                        v => !!v || this.$t('corporate.captable.form.percentual.required'),
                        v => !(v < 0) || this.$t('corporate.captable.form.percentual.length'),
                        v => (v <= 100) || this.$t('corporate.captable.form.percentual.lengthMore')
                    ]
                }
            },
            baseCaptable: undefined,
            captable: {
                objectId: undefined,
                title: undefined,
                client: undefined,
                createdBy: undefined,
                companyType: undefined,
                isCurrent: undefined,
                partners: []
            },
            initialShares: 0,
            baseShares: 0,
            baseSharesBlock: false,
            avaibleShares: 0,
            sumShares: 0,
            sumPercentual: 0,
            lastChanged: undefined,
            lastRemovedPercentual: undefined,
            groupEntries: false,
            sharePrevious: 0,
            shareFixed: 0,
            percentageFixed: 0,
            percentageAdded: 0,
            shareToAdd: 0,
            resultAll: 0,
            previousPartners: [],
            baseSharesPreviou: 0,
            showGroupEntries: true
        }
    },
    watch: {
        dialogState: function (val, oldVal) {
            if (val === true) {
                this.init()
            }
        },
        groupEntries: {
            handler: function (val, oldVal) {
                if (val === true) {
                    if (this.baseSharesBlock === true) {
                        this.baseSharesBlock = false
                    }
                    this.previousPartners = JSON.parse(JSON.stringify(this.captable.partners))
                    this.baseSharesPrevious = this.baseShares
                } else {
                    for (let index = 0; index < this.captable.partners.length; index++) {
                        this.captable.partners[index].new = false
                        this.captable.partners[index].raising = false
                    }
                    this.returnToInit()
                }
            },
            deep: true
        }
    },
    mounted () {},
    computed: {
        buttonColor () {
            return (!this.isNew) ? 'secondary' : 'primary'
        },
        buttonLabel () {
            return (!this.isNew) ? this.$t('corporate.captable.form.buttonLabelEdit') : this.$t('corporate.captable.form.buttonLabelAdd')
        },
        formRef () {
            return (!this.isNew) ? 'formCaptableEdit' : 'formCaptableAdd'
        },
        groupEntriesReady () {
            return (this.hasNews() && this.checkAllNewsHasValues())
        },
        hasDirtys () {
            return this.captable.partners.reduce((accumulator, element) => {
                if (element.dirty === true) {
                    return (accumulator += 1)
                } else {
                    return (accumulator += 0)
                }
            }, 0)
        }
    },
    methods: {
        init () {
            this.baseCaptable = JSON.parse(JSON.stringify(this.captable))
            if (!this.isNew) {
                this.$refs.formCaptableEdit.reset()
                this.form.title = this.$t('corporate.captable.form.titleDetail')
                this.captable = JSON.parse(JSON.stringify(this.currentCaptable))
            } else {
                this.$refs.formCaptableAdd.reset()
                if (this.currentCaptable !== undefined) {
                    this.captable.partners = JSON.parse(JSON.stringify(this.currentCaptable.partners))
                    this.previousPartners = JSON.parse(JSON.stringify(this.captable.partners))
                }
            }
            this.$nextTick(() => {
                this.initialShares = (this.client.shareCapital === undefined || this.client.shareCapital === null) ? 0 : this.client.shareCapital
                if (this.companyInfo.companyType !== 1) {
                    this.form.memberType = this.$t('corporate.captable.form.memberTypeShareholders')
                    this.form.shareType = this.$t('corporate.captable.form.shareTypeQuotas')
                    this.form.labels.shareCapitalInitial = this.$t('corporate.captable.form.shareCapitalInitial')
                    this.form.labels.shareCapitalBase = this.$t('corporate.captable.form.shareCapitalBase')
                }
                this.baseShares = JSON.parse(JSON.stringify(this.initialShares.toString()))
                this.avaibleShares = this.initialShares
                this.showGroupEntries = (this.initialShares > 0 && !this.baseSharesBlock)
                this.addOrDeleteDirtyProp(true)
                this.calcTotal()
                this.sharePrevious = parseInt(this.baseShares)
                this.form.loading = false
            })
        },
        releaseForm () {
            this.btnDisable = false
            this.form.valid = true
        },
        close () {
            this.btnDisable = false
            this.dialogState = false
            this.captable = JSON.parse(JSON.stringify(this.baseCaptable))
        },
        addPartner () {
            this.captable.partners.push({
                name: '',
                shares: undefined,
                percentual: undefined,
                raisingPercentual: undefined,
                dirty: false,
                new: true,
                raising: false
            })
            this.checkAddPartnerIsAvaible()
            if (this.groupEntries === false) {
                this.showGroupEntries = false
            }
        },
        deletePartner (index, partner) {
            this.lastRemovedPercentual = this.captable.partners[index].percentual
            this.captable.partners.splice(index, 1)
            // this.previousPartners.splice(index, 1)
            if (!this.baseSharesBlock) {
                this.incrementShares(false)
            }
            if (!this.groupEntries) {
                this.recalcRows(index, true)
                this.calcTotal()
            }
            // this.captable.partners = JSON.parse(JSON.stringify(this.previousPartners))
            this.checkAddPartnerIsAvaible()
            if (this.groupEntries === false && this.hasNews() === 0) {
                this.showGroupEntries = true
            }
        },
        addOrDeleteDirtyProp (boolean) {
            if (boolean) {
                this.addDirtyProp()
            } else {
                this.deleteDirtyProp()
            }
        },
        addDirtyProp () {
            for (let index = 0; index < this.captable.partners.length; index++) {
                this.captable.partners[index].dirty = false
            }
        },
        deleteDirtyProp () {
            for (let index = 0; index < this.captable.partners.length; index++) {
                delete this.captable.partners[index].dirty
                delete this.captable.partners[index].raising
                delete this.captable.partners[index].raisingPercentual
            }
        },
        turnNewFalse () {
            for (let index = 0; index < this.captable.partners.length; index++) {
                this.captable.partners[index].new = false
            }
        },
        disabledSave () {
            return !this.form.valid || (this.baseSharesBlock && this.sumShares > this.baseShares) || this.captable.partners.length <= 0
        },
        showToggleDirty (index) {
            return (
                (!isNaN(this.captable.partners[index].shares) && parseInt(this.captable.partners[index].shares) !== 0) &&
                (!isNaN(this.captable.partners[index].shares) && parseFloat(this.captable.partners[index].percentual) !== 0) &&
                this.initialShares !== 0 &&
                this.groupEntries &&
                !this.captable.partners[index].new
            )
        },
        toggleDirty (index) {
            if (
                (this.captable.partners[index].shares !== undefined && parseInt(this.captable.partners[index].shares) !== 0) &&
                (this.captable.partners[index].percentual !== undefined && parseFloat(this.captable.partners[index].percentual) !== 0.00)
            ) {
                this.captable.partners[index].dirty = !this.captable.partners[index].dirty
            } else {
                this.captable.partners[index].dirty = false
            }
            this.checkAddPartnerIsAvaible()
            this.$forceUpdate()
        },
        toggleNew (index) {
            this.captable.partners[index].new = !this.captable.partners[index].new
            this.captable.partners[index].raising = !this.captable.partners[index].raising
            this.$forceUpdate()
        },
        toggleRaising (index) {
            this.captable.partners[index].raising = !this.captable.partners[index].raising
            this.$forceUpdate()
        },
        toggleBlock () {
            this.baseSharesBlock = !this.baseSharesBlock
            this.$forceUpdate()
        },
        checkAddPartnerIsAvaible () {
            this.addPartnerAvaible = true
            if (this.captable.partners.length > 0) {
                if (this.captable.partners.length === 1) {
                    if (this.captable.partners[0].dirty) {
                        if (this.captable.partners[0].percentual >= 100) {
                            this.addPartnerAvaible = false
                        }
                    }
                } else {
                    if (this.returnSumPercentualDirty() >= 100) {
                        this.addPartnerAvaible = false
                    }
                }
            }
        },
        incrementShares (axis) {
            let a = parseInt(this.returnSumShares())
            let b = parseInt(this.baseShares)
            let dif = (a > b) ? (a - b) : (b - a)
            if (axis) {
                this.baseShares = (b + dif).toString()
            } else {
                let value = (b - dif).toString()
                this.baseShares = value
            }
        },
        returnSumShares () {
            let returnSumShares = 0
            for (let index = 0; index < this.captable.partners.length; index++) {
                let share = (this.captable.partners[index].shares === undefined || this.captable.partners[index].shares === Infinity) ? 0 : parseInt(this.captable.partners[index].shares)
                returnSumShares += share
            }
            return returnSumShares
        },
        returnMaxPercentual (except) {
            let returnSumPercentual = 0
            for (let index = 0; index < this.captable.partners.length; index++) {
                if (index !== except) {
                    let percentual = (this.captable.partners[index].percentual === undefined || this.captable.partners[index].percentual === Infinity) ? 0 : parseFloat(this.captable.partners[index].percentual)
                    returnSumPercentual += percentual
                }
            }
            return (100 - returnSumPercentual)
        },
        returnSumNewPercentual () {
            let returnSumPercentual = 0
            for (let index = 0; index < this.captable.partners.length; index++) {
                if (this.captable.partners[index].new) {
                    let percentual = (this.captable.partners[index].percentual === undefined || this.captable.partners[index].percentual === Infinity) ? 0 : parseInt(this.captable.partners[index].percentual)
                    returnSumPercentual += percentual
                }
            }
            return returnSumPercentual
        },
        calcByRow (index, isShare, recalcRows, percentManual) {
            if (this.captable.partners[index].shares !== undefined || this.captable.partners[index].percentual !== undefined) {
                if (!isShare) {
                    // evitar que possa colocar um valor invalido quando está iniciando o societario
                    if (this.initialShares === 0 && index === 0) {
                        this.captable.partners[index].percentual = parseFloat(100).toFixed(2)
                    } else {
                        // evitar que possa colocar um valor invalido
                        if (this.captable.partners[index].percentual <= 0) {
                            this.captable.partners[index].percentual = 0.01
                        }
                        if (this.captable.partners[index].percentual > 100) {
                            this.captable.partners[index].percentual = parseFloat(100).toFixed(2)
                        }
                    }
                    this.calcByPercent(index, false, recalcRows)
                } else {
                    this.captable.partners[index].shares = (parseInt(this.captable.partners[index].shares) === 0) ? 1 : this.captable.partners[index].shares
                    this.solvePercent(index, this.captable.partners[index].shares, percentManual)
                }
                if (recalcRows) this.recalcRows(index, isShare)
                this.calcTotal()
            }
        },
        calcByPercent (index, notSolvePercent, recalc) {
            // para evitar erro de calculo
            if (this.baseShares <= 0 || this.baseShares === Infinity) {
                this.baseShares = (this.initialShares <= 0) ? 1 : this.initialShares
            }

            // let percent = 0
            // caso está iniciando o societario o percentual é o definido no campo
            // if (this.initialShares === 0) {
            //     percent = this.captable.partners[index].percentual / 100
            // } else {
            //     // percent = this.returnSumNewPercentual(index) / 100
            //     // após o inicio a base de calculo altera dependendo do tipo de entrada
            //     // para entrada bloquada, necessita manter o percentual
            //     percent = this.captable.partners[index].percentual / 100
            //     // if (this.groupEntries) {
            //     //     // para entrada nao bloquada, o percentual é calculado a partir da soma das novas entradas
            //     //     percent = this.returnSumNewPercentual(index) / 100
            //     // }

            //     // esse calculo é quando não deseja ter alteração da quantidade de shares
            // }
            let percent = this.captable.partners[index].percentual / 100
            // console.log('percentual para calculo: ', percent)

            // de acordo com o excel para achar o share precisa fazer um caluclo que é tem a divisão a seguir
            let pDivider = 1 - percent
            // porém pode resultar em zero e para não ocorrer erro coloca 1
            if (pDivider === 0) {
                pDivider = 1
            }

            // console.log('divisor de acordo com a tabela do excel: ', pDivider)

            this.captable.partners[index].shares = (parseInt(this.captable.partners[index].shares) === 0 || this.captable.partners[index].shares === undefined) ? 0 : this.captable.partners[index].shares
            // let shares = this.initialShares - parseInt(this.captable.partners[index].shares)
            let shares = this.baseShares - parseInt(this.captable.partners[index].shares)
            if (this.groupEntries) {
                shares = this.initialShares - parseInt(this.captable.partners[index].shares)

                if (this.captable.partners[index].dirty) {
                    shares = this.baseShares - parseInt(this.captable.partners[index].shares)
                }
            }

            if (!this.baseSharesBlock) {
                shares = Math.round(shares * (percent / pDivider))
                if (this.groupEntries && !this.captable.partners[index].dirty) {
                    // shares = this.initialShares
                    shares = this.baseShares
                    shares = Math.round(shares * percent)
                }
            } else {
                shares = Math.round((this.captable.partners[index].percentual / 100) * this.baseShares)
                // console.log('baseblock: ', shares, this.captable.partners[index].percentual, this.baseShares)
                // shares = Math.round((this.captable.partners[index].percentual / 100) * this.initialShares)
                // console.log('baseblock: ', shares, this.captable.partners[index].percentual, this.initialShares)
            }

            // if (this.baseSharesBlock) {
            //     shares = this.baseShares - parseInt(this.captable.partners[index].shares)
            // } else {
            // value = this.initialShares
            // if (this.groupEntries) {
            //     if (this.captable.partners[index].dirty) {
            //         value = this.baseShares - this.captable.partners[index].shares
            //     } else {
            //         value = this.initialShares
            //     }
            // } else {
            //     if (this.hasDirtys()) {
            //         if (this.captable.partners[index].dirty) {
            //             value = this.baseShares - this.captable.partners[index].shares
            //         } else {
            //             value = this.baseShares - this.returnSumSharesDirty()
            //         }
            //     } else {
            //         if (this.captable.partners[index].dirty) {
            //             value = this.baseShares - this.captable.partners[index].shares
            //         } else {
            //             value = this.baseShares - this.returnSumSharesDirty()
            //         }
            //     }
            // }
            //     console.log('values: ', this.baseShares)
            //     shares = this.baseShares
            //     console.log('value: ', shares)
            // }

            // if (value === 0) {
            //     console.log('f')
            //     value = this.initialShares
            // }

            // console.log('o calculo do share para entrada em grupo (qtdShares * (percent / pDivider))')
            // let shares = parseInt(this.baseShares)

            // console.log('shares do calculo: ', shares)

            // if (this.initialShares !== 0) {
            // shares = (shares + this.initialShares) - this.captable.partners[index].shares
            // shares = Math.round((this.captable.partners[index].percentual / 100) * shares)
            // if (!this.captable.partners[index].dirty) {
            //     if (this.groupEntries && this.captable.partners[index].new) {
            //         // shares = shares + this.baseShares
            //         shares = shares + this.initialShares
            //         shares = Math.round((this.captable.partners[index].percentual / 100) * shares)
            //     } else {
            //         shares = shares + this.initialShares
            //         shares = Math.round((this.captable.partners[index].percentual / 100) * shares)
            //     }
            // }
            // }

            // if (this.baseSharesBlock) {
            //     shares = Math.round(percent)
            // }

            // console.log('recalc', recalc)
            // if (recalc) {
            //     this.newCalc(parseFloat(this.captable.partners[index].percentual))
            //     shares = Math.round(parseFloat(this.captable.partners[index].percentual / 100) * this.resultAll)
            // } else {
            //     if (this.captable.partners[index].dirty) {
            //         shares = Math.round(parseFloat(this.captable.partners[index].percentual / 100) * this.resultAll)
            //     }
            // }

            this.captable.partners[index].shares = shares
            if (!notSolvePercent) {
                this.solvePercent(index, shares)
            }

            // console.log('Funcionando no caso normal\n')
            // console.log('Inserindo nomes sem ter shares iniciais\n')
            // console.log('Funcionando no caso normal\n')
            // console.log('Inserindo nomes já tendo shares iniciais\n')
            // console.log('-------------------------\n')
        },
        solvePercent (index, shares, percentManual) {
            // console.log('index: ', index)
            let bs = this.baseShares
            if (!this.baseSharesBlock) {
                let ss = (percentManual) ? this.returnSumShares() - shares : this.returnSumShares()
                if (ss > bs) {
                    this.incrementShares(true)
                } else {
                    this.incrementShares(false)
                }
                bs = this.baseShares
            }
            let percentual = ((shares / bs) * 100)
            // console.log('resolvendo percentual: ', index, shares, bs, percentual)
            this.captable.partners[index].percentual = parseFloat(percentual).toFixed(2)
        },
        recalcRows (except, isShare, recalc) {
            if (this.baseSharesBlock) {
                let sumPercentualNotDirty = null
                let addingMember = true
                if (this.captable.partners[except] !== undefined) {
                    let maxP = this.returnMaxPercentual(except)
                    // console.log('aqui0: ', this.captable.partners[except].percentual, maxP, (this.captable.partners[except].percentual > maxP))
                    if (this.captable.partners[except].percentual > maxP) {
                        sumPercentualNotDirty = this.returnSumPercentual(except)
                        // console.log('aqui1: ', parseFloat(this.captable.partners[except].percentual), sumPercentualNotDirty)
                        sumPercentualNotDirty = parseFloat(100) * parseFloat(this.captable.partners[except].percentual) / parseFloat(sumPercentualNotDirty)
                    }
                } else {
                    sumPercentualNotDirty = this.returnSumPercentual()
                    sumPercentualNotDirty = parseFloat(100) * parseFloat(this.lastRemovedPercentual) / parseFloat(sumPercentualNotDirty)
                    addingMember = false
                }

                for (let index = 0; index < this.captable.partners.length; index++) {
                    if (this.captable.partners[index].dirty !== true) {
                        if (index !== except) {
                            if (sumPercentualNotDirty !== null) {
                                let newPercentual = this.returnNewPercentual(parseInt(this.captable.partners[index].shares), parseFloat(this.captable.partners[index].percentual), sumPercentualNotDirty, addingMember)
                                this.captable.partners[index].percentual = parseFloat(newPercentual).toFixed(2)
                            }
                        }
                    }
                    this.calcByRow(index, false, false, false)
                }
            } else {
                for (let index = 0; index < this.captable.partners.length; index++) {
                    if (this.captable.partners[index].dirty === true) {
                        if (index !== except) {
                            this.calcByRow(index, false, false, true)
                        }
                    }
                    // if (this.initialShares !== 0) {
                    //     console.log('lpkpkasp')
                    //     if (this.captable.partners[index].new) {
                    //         console.log('lununu')
                    //         // esse bloco quebra quando é entrada em grupo e tem um cara bloqueado
                    //         // if (index !== except) {
                    //         //     console.log('amsmamks')
                    //         //     this.calcByRow(index, false, false, true)
                    //         // }
                    //     }
                    // }
                    this.calcByRow(index, true, false)
                }
            }
            this.lastRemovedPercentual = undefined
        },
        returnSumPercentual (except) {
            let arrayCheck = this.captable.partners
            if (except) {
                arrayCheck = arrayCheck.filter((value, index) => { return except !== index })
            }
            return arrayCheck.reduce((accumulator, element) => {
                if (element.dirty === false) {
                    return (accumulator += parseFloat(element.percentual))
                } else {
                    return (accumulator += 0)
                }
            }, 0)
        },
        returnSumPercentualDirty () {
            return this.captable.partners.reduce((accumulator, element) => {
                if (element.dirty === true) {
                    return (accumulator += parseFloat(element.percentual))
                } else {
                    return (accumulator += 0)
                }
            }, 0)
        },
        returnSumSharesDirty () {
            return this.captable.partners.reduce((accumulator, element) => {
                if (element.dirty === true) {
                    return (accumulator += parseInt(element.shares))
                } else {
                    return (accumulator += 0)
                }
            }, 0)
        },
        returnNewPercentual (a, b, c, addingMember) {
            // console.log('aa', a, b, c, this.baseShares)
            if (addingMember) {
                // console.log('a', (a - ((a / 100) * c)) * b / a)
                return (a - ((a / 100) * c)) * b / a
            } else {
                // console.log('b', (a + (a * c / 100)) / this.baseShares * 100)
                return (a + (a * c / 100)) / this.baseShares * 100
            }
        },
        hasNews () {
            return this.captable.partners.reduce((accumulator, element) => {
                if (element.new === true) {
                    return (accumulator += 1)
                } else {
                    return (accumulator += 0)
                }
            }, 0)
        },
        checkAllNewsHasValues () {
            let hasValues = false
            for (const partner of this.captable.partners) {
                if (partner.new) {
                    const share = parseInt(partner.shares)
                    const percent = parseFloat(partner.percentual)
                    if (
                        (share !== undefined && !isNaN(share) && isFinite(share)) ||
                        (percent !== undefined && !isNaN(percent) && isFinite(percent))
                    ) {
                        hasValues = true
                    } else {
                        hasValues = false
                        break
                    }
                }
            }
            return hasValues
        },
        returnSumShareOrPercentual (flag, property, isFloat) {
            const value = this.captable.partners.reduce((accumulator, element) => {
                if (element[flag] === true) {
                    let value = (isNaN(element[property])) ? 0 : element[property]
                    return (accumulator += (isFloat) ? parseFloat(value) : parseInt(value))
                } else {
                    return (accumulator += 0)
                }
            }, 0)
            if (isNaN(value)) return 0
            return (isFloat) ? parseFloat(value) : parseInt(value)
        },
        returnSumPercentualNew () {
            return this.captable.partners.reduce((accumulator, element) => {
                if (element.new === true) {
                    if (element.raising === true) {
                        return (accumulator += parseFloat(element.raisingPercentual))
                    } else {
                        return (accumulator += parseFloat(element.percentual))
                    }
                } else {
                    return (accumulator += 0)
                }
            }, 0)
        },
        calcTotal () {
            this.sumShares = 0
            this.sumPercentual = 0
            for (let index = 0; index < this.captable.partners.length; index++) {
                let share = (this.captable.partners[index].shares === undefined || isNaN(this.captable.partners[index].shares) || !isFinite(this.captable.partners[index].shares)) ? 0 : parseInt(this.captable.partners[index].shares)
                this.sumShares += share
                let percentual = (this.captable.partners[index].percentual === undefined || isNaN(this.captable.partners[index].percentual) || !isFinite(this.captable.partners[index].percentual)) ? 0 : parseFloat(this.captable.partners[index].percentual)
                if (this.captable.partners[index].raising) {
                    percentual = (this.captable.partners[index].raisingPercentual === undefined || isNaN(this.captable.partners[index].raisingPercentual) || !isFinite(this.captable.partners[index].raisingPercentual)) ? 0 : parseFloat(this.captable.partners[index].raisingPercentual)
                }
                this.sumPercentual += percentual
            }
            this.sumPercentual = Math.round(this.sumPercentual).toFixed(2)
            this.sumPercentual = (parseInt(this.sumPercentual) > 100) ? parseFloat(100).toFixed(2) : this.sumPercentual
        },
        calc () {
            if (this.hasNews() && !this.checkAllNewsHasValues()) { return }
            let sumPercentualNew = parseFloat(this.returnSumPercentualNew()) / 100
            let newShares = Math.round(this.baseShares * (sumPercentualNew / (1 - sumPercentualNew)))
            this.baseShares = parseInt(this.baseShares) + parseInt(newShares)
            // console.log('newShares', this.baseShares)

            for (let index = 0; index < this.captable.partners.length; index++) {
                if (this.captable.partners[index].new === true) {
                    this.calcByPercent(index, true)
                }
            }
            this.recalcRows()
        },
        newCalc (percentage) {
            let ready = true
            this.percentageFixed = this.returnSumPercentualDirty() + this.returnSumShareOrPercentual('raising', 'raisingPercentual', true)
            // this.percentageAdded = (percentage !== undefined) ? percentage : this.returnSumShareOrPercentual('new', 'percentual', true) + this.returnSumShareOrPercentual('changed', 'percentual', true)
            this.percentageAdded = (percentage !== undefined) ? percentage : this.returnSumShareOrPercentual('new', 'percentual', true)

            if (this.percentageFixed >= 100) {
                alert(this.$t('corporate.captable.form.fixedError'))
                ready = false
            }

            if (this.percentageAdded >= 100) {
                alert(this.$t('corporate.captable.form.addedError'))
                ready = false
            }

            if (parseFloat(this.percentageFixed) + parseFloat(this.percentageAdded) >= 100) {
                alert(this.$t('corporate.captable.form.fixedAddedError'))
                ready = false
            }

            if (ready) {
                this.shareFixed = this.returnSumSharesDirty() + this.returnSumShareOrPercentual('raising', 'shares', false)

                let a = ((parseFloat(this.percentageFixed) + parseFloat(this.percentageAdded)) / 100)
                let b = parseInt(this.sharePrevious) - parseInt(this.shareFixed)

                this.shareToAdd = Math.round(b * a / (1 - a))
                this.resultAll = b + this.shareToAdd
                this.baseShares = this.resultAll

                // console.log('percentageFixed', this.percentageFixed)
                // console.log('percentageAdded', this.percentageAdded)
                // console.log('% entrando ou fixo', a)
                // console.log('Q antes rodada', this.sharePrevious)
                // console.log('Q fixos', this.shareFixed)
                // console.log('Q sobraram', b)
                // console.log('Q a adicionar', this.shareToAdd)
                // console.log('Quotas novas', this.resultAll)
            }
            return ready
        },
        calcGroup () {
            // armazenar o valor anterior do fixado para multiplicar pelas novas quantidades de cotas
            if (this.newCalc()) {
                for (let index = 0; index < this.captable.partners.length; index++) {
                    if (this.captable.partners[index].percentual === undefined) return
                    if (this.captable.partners[index].dirty || this.captable.partners[index].new) {
                        this.captable.partners[index].shares = Math.round((this.captable.partners[index].percentual / 100) * this.baseShares)
                    } else if (this.captable.partners[index].raising) {
                        this.captable.partners[index].shares = Math.round((this.captable.partners[index].raisingPercentual / 100) * this.baseShares)
                    } else {
                        this.captable.partners[index].shares = (parseInt(this.captable.partners[index].shares) === 0) ? 1 : this.captable.partners[index].shares
                        let percentual = ((this.captable.partners[index].shares / this.baseShares) * 100)
                        this.captable.partners[index].percentual = parseFloat(percentual).toFixed(2)
                    }
                }
                this.calcTotal()
            }
        },
        returnToInit () {
            this.captable.partners = JSON.parse(JSON.stringify(this.previousPartners))
            this.baseShares = this.baseSharesPrevious
            this.calcTotal()
        },
        async save () {
            this.addOrDeleteDirtyProp(false)
            this.turnNewFalse()
            let currentUserCompany = JSON.parse(localStorage.getItem('currentUserCompany'))
            let params = {
                isNew: true,
                objectId: undefined,
                clientId: this.$helpers.getCorporateClientId(this.$route.name, 'ClienteSocietarioCaptable', this.$route.params.id),
                title: this.captable.title,
                companyType: this.companyInfo.companyType,
                partners: this.captable.partners,
                shareCapital: this.baseShares,
                profile: currentUserCompany.objectId
            }
            if (this.currentCaptable !== undefined) {
                params.objectId = this.currentCaptable.objectId
            }
            if (!this.isNew) {
                if (!this.$refs.formCaptableEdit.validate()) {
                    this.releaseForm()
                    return
                }
                params.isNew = false
            } else {
                if (!this.$refs.formCaptableAdd.validate()) {
                    this.releaseForm()
                    return
                }
            }
            if (this.captable.partners.length <= 0) {
                alert(this.$t('corporate.captable.form.missingMembers'))
                return
            }
            this.form.loading = true
            this.btnDisable = true
            let endpoint = API.EndPoints.captableRegister
            await new API(endpoint, params).request()
                .then(() => {
                    this.$emit('close-captable-success-dialog')
                })
                .catch(error => {
                    if (error.status !== 0) alert(this.$t('common.errorSave'))
                })
            this.close()
        }
    }
}
</script>

<style lang="stylus" scoped>
    .member-container
        margin-bottom: 110px;
    .captable-total-container
        background-color: #f6f8f9
        position: fixed
        width: 100%
        left: 0
        right: 0
        bottom: 0
        outline: none
        z-index: 10
    .text-color-red
        color: red
</style>
